/**
 * @fileoverview Media component for rendering FeaturedMedia (Image or Video)
 * @module screen-view-v2/components/shared/Media
 *
 * V2 adaptation:
 * - FeaturedMedia replaces MediaType
 * - media.appearance (FeaturedMediaStyle) replaces media.styles
 * - media.appearance.dimensions?.heightPercentage replaces media.styles.heightPercentage
 * - media.appearance.cornerRadii (CornerRadius) replaces media.styles.mainCornerRadius (number)
 * - mapFrameLayoutStyles(media.layout) replaces mapBoxBlockStyles(media.box.styles)
 * - Type narrowing via media.kind to access FeaturedImageContent / FeaturedClipContent
 */

import React, { memo } from 'react';
import type { CSSProperties } from 'react';
import type {
  FeaturedMedia,
  FeaturedImageContent,
  FeaturedClipContent,
} from '@segmently/schema-models/v2';
import { Image } from '../Image';
import type { ImageLoadingHints } from '../Image';
import { Video } from '../Video';
import type { ArticleVideoOptions } from '../Video/Video';
import { mapFrameLayoutStyles, mapCornerRadiusToCSS, mapMediaGradientOverlayToCSS } from '../../../utils/styleMapper';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import { useElementAnimation, mergeAnimationClassName } from '../../../animations';
import { mediaRenders } from '../../../utils/renderPredicates';
import type { PreviewMode } from '../../../types/previewMode';
import styles from './Media.module.css';

export interface MediaProps {
  media: FeaturedMedia | undefined;
  locale: string;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  fillContainer?: boolean;
  scaleMode?: string;
  previewMode?: PreviewMode;
  /** Article template (G2, D6): resolved alt text; absent = today's alt="". */
  alt?: string;
  /** Article template (G2, D6): native article video options; absent = today's funnel video. */
  videoOptions?: ArticleVideoOptions;
  /** Article template (G5, R4): image loading hints from ArticleFigure; absent = today's <img>. */
  imageLoadingHints?: ImageLoadingHints;
}

export const Media = memo(function Media({
  media,
  locale,
  className,
  'data-testid': testId,
  editPath,
  fillContainer = false,
  scaleMode: scaleModeOverride,
  previewMode,
  alt,
  videoOptions,
  imageLoadingHints,
}: MediaProps): React.ReactElement | null {
  const mediaAnim = useElementAnimation(editPath);

  // Renders nothing unless it is an Image with its image or a Video with its clip (shared with the
  // entrance plan: utils/renderPredicates.ts)
  if (!mediaRenders(media)) {
    return null;
  }

  const { kind, content, appearance } = media;

  // V2: appearance.scaleMode instead of styles.scaleMode
  const scaleMode = scaleModeOverride ?? appearance?.scaleMode;
  const repeat = appearance?.repeat;

  // V2: appearance.dimensions?.heightPercentage
  const heightPercentage = appearance?.dimensions?.heightPercentage;

  // V2: cornerRadii (CornerRadius object) instead of mainCornerRadius (number)
  const cornerRadiusStyles = mapCornerRadiusToCSS(appearance?.cornerRadii);
  const hasCornerRadius = Object.keys(cornerRadiusStyles).length > 0;

  // V2: mapFrameLayoutStyles replaces mapBoxBlockStyles
  const layoutStyles: CSSProperties = mapFrameLayoutStyles(media.layout);

  const wrapperStyle: CSSProperties = {
    ...layoutStyles,
    ...(heightPercentage != null && {
      maxHeight: `${heightPercentage}%`,
      minHeight: `${heightPercentage}%`,
    }),
    ...(hasCornerRadius && {
      ...cornerRadiusStyles,
      overflow: 'hidden',
    }),
    ...mediaAnim.style,
  };
  const wrapperClassName = mergeAnimationClassName(styles.mediaWrapper, mediaAnim);

  const schemaTestId = testId ?? getSchemaTestId(editPath);

  const gradientStyle = mapMediaGradientOverlayToCSS(appearance?.gradient);

  // Image media
  if (kind === 'Image') {
    const imageContent = content as FeaturedImageContent;

    return (
      <div className={wrapperClassName} style={wrapperStyle} data-anim={mediaAnim['data-anim']} data-testid={schemaTestId} data-edit-path={editPath}>
        <Image
          image={imageContent.image}
          locale={locale}
          className={className ? `${styles.image} ${className}` : styles.image}
          editPath={editPath}
          skipElementAnimation
          fillContainer={fillContainer}
          scaleMode={scaleMode}
          previewMode={previewMode}
          alt={alt}
          loadingHints={imageLoadingHints}
        />
        {gradientStyle && (
          <div className={styles.gradient} style={gradientStyle} data-testid="media-gradient" />
        )}
      </div>
    );
  }

  // Video media
  if (kind === 'Video') {
    const clipContent = content as FeaturedClipContent;

    return (
      <div className={wrapperClassName} style={wrapperStyle} data-anim={mediaAnim['data-anim']} data-testid={schemaTestId} data-edit-path={editPath}>
        <Video
          clip={clipContent.video}
          locale={locale}
          className={className ? `${styles.video} ${className}` : styles.video}
          editPath={editPath}
          fillContainer={fillContainer}
          scaleMode={scaleMode}
          repeat={repeat}
          previewMode={previewMode}
          articleOptions={videoOptions}
        />
        {gradientStyle && (
          <div className={styles.gradient} style={gradientStyle} data-testid="media-gradient" />
        )}
      </div>
    );
  }

  return null;
});

Media.displayName = 'Media';
